// userApiService.ts - UPDATED VERSION with changePassword method and company field
import api from '@/services/api'
import type { User, UserType, UserStatus, UserFormData, CompanyType } from '@/types/user'

// Types for backend responses
interface BackendUser {
  id: string
  username: string
  userType: UserType
  company?: CompanyType
  first_name: string
  last_name: string
  email: string
  phone?: string
  status: UserStatus
  department?: string
  address?: string
  city?: string
  state?: string
  clientType?: string
  industry?: string
  contactPerson?: string
  alternatePhone?: string
  profileImage?: string
  lastLogin?: string
  emailVerified?: boolean
  notes?: string
  createdAt: string
  updatedAt?: string
  hasPassword?: boolean
}

interface BackendResponse {
  success: boolean
  message?: string
  error?: string
  user?: BackendUser
  users?: BackendUser[]
  data?: BackendUser | BackendUser[] | unknown
  count?: number
}

// Type for update data
interface UpdateUserData {
  username?: string
  firstName?: string
  lastName?: string
  email?: string
  phone?: string
  userType?: UserType
  company?: CompanyType
  department?: string
  status?: UserStatus
  notes?: string
  address?: string
  city?: string
  state?: string
  password?: string
}

// Type for create user backend data - UPDATED for email invitation
interface CreateUserBackendData {
  email: string
  firstName?: string
  lastName?: string
  phone?: string
  userType?: UserType
  company?: CompanyType
  department?: string
  notes?: string
  password?: string // Optional for email invitation
}

// Type for change password data
interface ChangePasswordData {
  currentPassword: string
  newPassword: string
}

// User statistics interface
interface UserStats {
  total: number
  admin: number
  agent: number
  engineer: number
  client: number
  active: number
  inactive: number
  pending: number
  suspended: number
}

// Token verification response
interface TokenVerificationData {
  email: string
  username: string
  firstName: string
}

interface ApiErrorResponse {
  response?: {
    data?: {
      message?: string
      error?: string
    }
  }
}

export class UserApiService {
  // Get all users - UPDATED
  static async getUsers(): Promise<User[]> {
    try {
      console.log('🔍 Fetching users from backend...')

      const response = await api.get<BackendResponse>('/api/users')

      if (!response.data.success) {
        throw new Error(response.data.message || response.data.error || 'Failed to fetch users')
      }

      // Handle both response formats: data.data for new format, users for old format
      const users = response.data.data as BackendUser[] || response.data.users || []
      console.log(`✅ Retrieved ${users.length} users from database`)

      // Map backend users to frontend format
      return users.map((user: BackendUser) => ({
        id: user.id,
        username: user.username,
        userType: user.userType,
        company: user.company,
        firstName: user.first_name || '',
        lastName: user.last_name || '',
        email: user.email,
        phone: user.phone,
        status: user.status,
        department: user.department,
        permissions: [],
        createdAt: user.createdAt,
        lastLogin: user.lastLogin,
        notes: user.notes,
        address: user.address,
        city: user.city,
        state: user.state,
        clientType: user.clientType,
        industry: user.industry,
        contactPerson: user.contactPerson,
        alternatePhone: user.alternatePhone,
        profileImage: user.profileImage,
        emailVerified: user.emailVerified,
        hasPassword: user.hasPassword || true,
      }))
    } catch (error: unknown) {
      console.error('❌ Error fetching users:', error)

      let errorMessage = 'Failed to fetch users'

      if (error instanceof Error) {
        errorMessage = error.message
      } else if (error && typeof error === 'object') {
        const apiError = error as ApiErrorResponse
        if (apiError.response?.data?.message) {
          errorMessage = apiError.response.data.message
        } else if (apiError.response?.data?.error) {
          errorMessage = apiError.response.data.error
        }
      }

      throw new Error(errorMessage)
    }
  }

  // Create a new user - UPDATED for email invitation
  static async createUser(userData: UserFormData): Promise<User> {
    try {
      console.log('📡 Creating new user...', userData)

      // Validate required fields - UPDATED: password is now optional
      if (!userData.email || !userData.firstName || !userData.lastName) {
        throw new Error('Email, firstName, and lastName are required')
      }

      // Prepare data for backend - NEW FORMAT for email invitation
      const backendData: CreateUserBackendData = {
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        phone: userData.phone,
        userType: userData.userType || 'agent',
        company: userData.company,
        department: userData.department,
        notes: userData.notes,
        password: userData.password // Optional - if provided, user gets immediate access
      }

      console.log('📦 Sending to backend:', backendData)

      const response = await api.post<BackendResponse>(
        '/api/users',
        backendData
      )

      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.message || response.data.error || 'Failed to create user')
      }

      const user = response.data.data as BackendUser
      console.log(`✅ User created successfully: ${user.email}`)

      // Return mapped user
      return {
        id: user.id,
        username: user.username,
        userType: user.userType,
        company: user.company,
        firstName: user.first_name || userData.firstName || '',
        lastName: user.last_name || userData.lastName || '',
        email: user.email,
        phone: user.phone,
        status: user.status,
        department: user.department,
        permissions: [],
        createdAt: user.createdAt,
        lastLogin: user.lastLogin,
        notes: user.notes,
        address: user.address,
        city: user.city,
        state: user.state,
        clientType: user.clientType,
        industry: user.industry,
        contactPerson: user.contactPerson,
        alternatePhone: user.alternatePhone,
        profileImage: user.profileImage,
        emailVerified: user.emailVerified,
        // hasPassword: user.hasPassword || !!userData.password,
      }
    } catch (error: unknown) {
      console.error('❌ Error creating user:', error)

      let errorMessage = 'Failed to create user'
      if (error instanceof Error) {
        errorMessage = error.message
      } else if (error && typeof error === 'object') {
        const apiError = error as ApiErrorResponse
        if (apiError.response?.data?.message) {
          errorMessage = apiError.response.data.message
        } else if (apiError.response?.data?.error) {
          errorMessage = apiError.response.data.error
        }
      }

      throw new Error(errorMessage)
    }
  }

  // Update user - UPDATED for user type change only
  static async updateUser(id: string, userData: UpdateUserData): Promise<User> {
    try {
      console.log('🔍 Updating user:', id, 'with data:', userData)

      // For edit mode, we only send userType (as per your requirement)
      const updateData: Record<string, unknown> = {}

      // Only update userType in edit mode (other fields are hidden)
      if (userData.userType !== undefined) {
        updateData.userType = userData.userType
      }

      // If company is provided (for admin updates)
      if (userData.company !== undefined) {
        updateData.company = userData.company
      }

      // If password is provided (for password reset or immediate creation)
      if (userData.password && userData.password.trim()) {
        updateData.password = userData.password
      }

      console.log('📦 Sending update data:', updateData)

      const response = await api.put<BackendResponse>(`/api/users/${id}`, updateData)

      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.message || response.data.error || 'Failed to update user')
      }

      console.log('✅ User updated successfully')

      const user = response.data.data as BackendUser
      return {
        id: user.id,
        username: user.username,
        userType: user.userType,
        company: user.company,
        firstName: user.first_name || '',
        lastName: user.last_name || '',
        email: user.email,
        phone: user.phone,
        status: user.status,
        department: user.department,
        permissions: [],
        createdAt: user.createdAt,
        lastLogin: user.lastLogin,
        notes: user.notes,
        address: user.address,
        city: user.city,
        state: user.state,
        clientType: user.clientType,
        industry: user.industry,
        contactPerson: user.contactPerson,
        alternatePhone: user.alternatePhone,
        profileImage: user.profileImage,
        emailVerified: user.emailVerified,
        // hasPassword: user.hasPassword || true,
      }
    } catch (error: unknown) {
      console.error('❌ Error updating user:', error)

      let errorMessage = 'Failed to update user'

      if (error instanceof Error) {
        errorMessage = error.message
      } else if (error && typeof error === 'object') {
        const apiError = error as ApiErrorResponse
        if (apiError.response?.data?.message) {
          errorMessage = apiError.response.data.message
        } else if (apiError.response?.data?.error) {
          errorMessage = apiError.response.data.error
        }
      }

      throw new Error(errorMessage)
    }
  }

  // NEW: Change password for logged-in user
  static async changePassword(data: ChangePasswordData): Promise<boolean> {
    try {
      console.log('🔍 Changing password...')

      if (!data.currentPassword) {
        throw new Error('Current password is required')
      }

      if (!data.newPassword) {
        throw new Error('New password is required')
      }

      if (data.newPassword.length < 6) {
        throw new Error('New password must be at least 6 characters')
      }

      const response = await api.post<BackendResponse>(
        '/api/users/change-password',
        data
      )

      if (!response.data.success) {
        throw new Error(response.data.message || response.data.error || 'Failed to change password')
      }

      console.log('✅ Password changed successfully')
      return true
    } catch (error: unknown) {
      console.error('❌ Error changing password:', error)

      let errorMessage = 'Failed to change password'

      if (error instanceof Error) {
        errorMessage = error.message
      } else if (error && typeof error === 'object') {
        const apiError = error as ApiErrorResponse
        if (apiError.response?.data?.message) {
          errorMessage = apiError.response.data.message
        }
      }

      throw new Error(errorMessage)
    }
  }

  // Update user status (activate/deactivate) - UPDATED endpoint
  static async updateUserStatus(id: string, status: UserStatus): Promise<boolean> {
    try {
      console.log(`🔍 Updating user ${id} status to: ${status}`)

      const response = await api.put<BackendResponse>(
        `/api/users/${id}/status`,
        { status }
      )

      if (!response.data.success) {
        throw new Error(response.data.message || response.data.error || 'Failed to update status')
      }

      console.log('✅ User status updated successfully')
      return true
    } catch (error: unknown) {
      console.error('❌ Error updating user status:', error)

      let errorMessage = 'Failed to update status'

      if (error instanceof Error) {
        errorMessage = error.message
      } else if (error && typeof error === 'object') {
        const apiError = error as ApiErrorResponse
        if (apiError.response?.data?.message) {
          errorMessage = apiError.response.data.message
        }
      }

      throw new Error(errorMessage)
    }
  }

  // Update user type - NEW METHOD
  static async updateUserType(id: string, userType: UserType): Promise<boolean> {
    try {
      console.log(`🔍 Updating user ${id} type to: ${userType}`)

      const response = await api.put<BackendResponse>(
        `/api/users/${id}/type`,
        { userType }
      )

      if (!response.data.success) {
        throw new Error(response.data.message || response.data.error || 'Failed to update user type')
      }

      console.log('✅ User type updated successfully')
      return true
    } catch (error: unknown) {
      console.error('❌ Error updating user type:', error)

      let errorMessage = 'Failed to update user type'

      if (error instanceof Error) {
        errorMessage = error.message
      } else if (error && typeof error === 'object') {
        const apiError = error as ApiErrorResponse
        if (apiError.response?.data?.message) {
          errorMessage = apiError.response.data.message
        }
      }

      throw new Error(errorMessage)
    }
  }

  // Delete user - UPDATED endpoint
  static async deleteUser(id: string): Promise<boolean> {
    try {
      console.log(`🔍 Deleting user: ${id}`)

      const response = await api.delete<BackendResponse>(`/api/users/${id}`)

      if (!response.data.success) {
        throw new Error(response.data.message || response.data.error || 'Failed to delete user')
      }

      console.log('✅ User deleted successfully')
      return true
    } catch (error: unknown) {
      console.error('❌ Error deleting user:', error)

      let errorMessage = 'Failed to delete user'

      if (error instanceof Error) {
        errorMessage = error.message
      } else if (error && typeof error === 'object') {
        const apiError = error as ApiErrorResponse
        if (apiError.response?.data?.message) {
          errorMessage = apiError.response.data.message
        }
      }

      throw new Error(errorMessage)
    }
  }

  // NEW: Setup password via email invitation
  static async setupPassword(token: string, password: string): Promise<boolean> {
    try {
      console.log('🔍 Setting up password with token...')

      if (!token || !password) {
        throw new Error('Token and password are required')
      }

      if (password.length < 6) {
        throw new Error('Password must be at least 6 characters')
      }

      const response = await api.post<BackendResponse>(
        '/api/users/setup-password',
        { token, password }
      )

      if (!response.data.success) {
        throw new Error(response.data.message || response.data.error || 'Failed to setup password')
      }

      console.log('✅ Password setup successful')
      return true
    } catch (error: unknown) {
      console.error('❌ Error setting up password:', error)

      let errorMessage = 'Failed to setup password'

      if (error instanceof Error) {
        errorMessage = error.message
      } else if (error && typeof error === 'object') {
        const apiError = error as ApiErrorResponse
        if (apiError.response?.data?.message) {
          errorMessage = apiError.response.data.message
        }
      }

      throw new Error(errorMessage)
    }
  }

  // NEW: Request password reset
  static async requestPasswordReset(email: string): Promise<boolean> {
    try {
      console.log(`🔍 Requesting password reset for: ${email}`)

      if (!email) {
        throw new Error('Email is required')
      }

      const response = await api.post<BackendResponse>(
        '/api/users/request-password-reset',
        { email }
      )

      if (!response.data.success) {
        throw new Error(response.data.message || response.data.error || 'Failed to request password reset')
      }

      console.log('✅ Password reset request sent')
      return true
    } catch (error: unknown) {
      console.error('❌ Error requesting password reset:', error)

      let errorMessage = 'Failed to request password reset'

      if (error instanceof Error) {
        errorMessage = error.message
      } else if (error && typeof error === 'object') {
        const apiError = error as ApiErrorResponse
        if (apiError.response?.data?.message) {
          errorMessage = apiError.response.data.message
        }
      }

      throw new Error(errorMessage)
    }
  }

  // NEW: Reset password with token
  static async resetPassword(token: string, password: string): Promise<boolean> {
    try {
      console.log('🔍 Resetting password with token...')

      if (!token || !password) {
        throw new Error('Token and password are required')
      }

      if (password.length < 6) {
        throw new Error('Password must be at least 6 characters')
      }

      const response = await api.post<BackendResponse>(
        '/api/users/reset-password',
        { token, password }
      )

      if (!response.data.success) {
        throw new Error(response.data.message || response.data.error || 'Failed to reset password')
      }

      console.log('✅ Password reset successful')
      return true
    } catch (error: unknown) {
      console.error('❌ Error resetting password:', error)

      let errorMessage = 'Failed to reset password'

      if (error instanceof Error) {
        errorMessage = error.message
      } else if (error && typeof error === 'object') {
        const apiError = error as ApiErrorResponse
        if (apiError.response?.data?.message) {
          errorMessage = apiError.response.data.message
        }
      }

      throw new Error(errorMessage)
    }
  }

  // NEW: Verify setup token
  static async verifySetupToken(token: string): Promise<TokenVerificationData> {
    try {
      console.log('🔍 Verifying setup token...')

      if (!token) {
        throw new Error('Token is required')
      }

      const response = await api.post<BackendResponse>(
        '/api/users/verify-setup-token',
        { token }
      )

      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.message || response.data.error || 'Invalid or expired token')
      }

      console.log('✅ Token verification successful')
      return response.data.data as TokenVerificationData
    } catch (error: unknown) {
      console.error('❌ Error verifying token:', error)

      let errorMessage = 'Failed to verify token'

      if (error instanceof Error) {
        errorMessage = error.message
      } else if (error && typeof error === 'object') {
        const apiError = error as ApiErrorResponse
        if (apiError.response?.data?.message) {
          errorMessage = apiError.response.data.message
        }
      }

      throw new Error(errorMessage)
    }
  }

  static async verifyResetToken(token: string): Promise<{
    success: boolean;
    isValid: boolean;
    message?: string;
    data?: {
      email: string;
      userId: string;
    };
  }> {
    try {
      console.log('🔍 [FRONTEND] Verifying reset token...');

      const response = await api.post('/api/users/verify-reset-token', { token });

      console.log('✅ [FRONTEND] Token verification response:', response.data);
      return response.data;
    } catch (error: unknown) {
      console.error('❌ [FRONTEND] Verify reset token error:', error);

      // Properly typed error handling without 'any'
      let errorMessage = 'Failed to verify reset token';

      if (error && typeof error === 'object') {
        const axiosError = error as {
          response?: {
            data?: {
              message?: string;
              error?: string;
              [key: string]: unknown;
            };
          };
          request?: unknown;
          message?: string;
        };

        if (axiosError.response?.data) {
          console.error('❌ [FRONTEND] Error details:', axiosError.response.data);
          errorMessage = axiosError.response.data.message ||
                        axiosError.response.data.error ||
                        'Failed to verify reset token';
        } else if (axiosError.request) {
          console.error('❌ [FRONTEND] No response received:', axiosError.request);
          errorMessage = 'Network error. Please check your connection.';
        } else if (axiosError.message) {
          console.error('❌ [FRONTEND] Request setup error:', axiosError.message);
          errorMessage = axiosError.message;
        }
      }

      // Return error structure similar to backend response
      return {
        success: false,
        isValid: false,
        message: errorMessage
      };
    }
  }

  // NEW: Get user statistics
  static async getUserStats(): Promise<UserStats> {
    try {
      console.log('🔍 Fetching user statistics...')

      const response = await api.get<BackendResponse>('/api/users/stats')

      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.message || response.data.error || 'Failed to fetch user statistics')
      }

      console.log('✅ User statistics retrieved')
      return response.data.data as UserStats
    } catch (error: unknown) {
      console.error('❌ Error fetching user statistics:', error)

      let errorMessage = 'Failed to fetch user statistics'

      if (error instanceof Error) {
        errorMessage = error.message
      } else if (error && typeof error === 'object') {
        const apiError = error as ApiErrorResponse
        if (apiError.response?.data?.message) {
          errorMessage = apiError.response.data.message
        }
      }

      throw new Error(errorMessage)
    }
  }

  // Delete user permanently - KEPT for backward compatibility
  static async deleteUserPermanently(id: string): Promise<boolean> {
    try {
      console.log(`🔍 Permanently deleting user: ${id}`)

      // Since we don't have a permanent delete endpoint, use regular delete
      const response = await api.delete<BackendResponse>(`/api/users/${id}`)

      if (!response.data.success) {
        throw new Error(response.data.message || response.data.error || 'Failed to delete user')
      }

      console.log('✅ User deleted')
      return true
    } catch (error: unknown) {
      console.error('❌ Error deleting user:', error)

      let errorMessage = 'Failed to delete user'

      if (error instanceof Error) {
        errorMessage = error.message
      } else if (error && typeof error === 'object') {
        const apiError = error as ApiErrorResponse
        if (apiError.response?.data?.message) {
          errorMessage = apiError.response.data.message
        }
      }

      throw new Error(errorMessage)
    }
  }

  // Test connection function
  static async testConnection(): Promise<boolean> {
    try {
      console.log('🔍 Testing connection...')

      const response = await api.get<BackendResponse>('/api/users')

      if (response.data.success) {
        console.log('✅ Connection test successful')
        return true
      } else {
        console.log('❌ Connection test failed:', response.data)
        return false
      }
    } catch (error: unknown) {
      console.error('❌ Connection test error:', error)
      return false
    }
  }
}
